import React, { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { XMarkIcon, PlayIcon, PlusIcon, PencilIcon, TrashIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import instructionService from "../../api/instructionService";
import AuthContext from "../../contexts/AuthContext";

const InstructionModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const { user } = useContext(AuthContext);
    const isAdmin = user?.role === 'admin';

    const [instructions, setInstructions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeVideo, setActiveVideo] = useState(null);

    // Admin state
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({ id: null, title: '', videoUrl: '', order: 0, file: null });

    useEffect(() => {
        if (isOpen) {
            fetchInstructions();
        }
    }, [isOpen]);

    const fetchInstructions = async () => {
        try {
            setLoading(true);
            const data = await instructionService.getAllInstructions();
            setInstructions(data);
        } catch (error) {
            console.error("Error fetching instructions:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('title', editData.title);
        formData.append('order', editData.order);
        if (editData.file) {
            formData.append('video', editData.file);
        } else if (editData.videoUrl) {
            formData.append('videoUrl', editData.videoUrl);
        }

        try {
            if (editData.id) {
                await instructionService.updateInstruction(editData.id, formData);
            } else {
                await instructionService.createInstruction(formData);
            }
            setIsEditing(false);
            setEditData({ id: null, title: '', videoUrl: '', order: 0, file: null });
            fetchInstructions();
        } catch (error) {
            console.error("Error saving instruction:", error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('instructions.admin.confirmDelete'))) {
            try {
                await instructionService.deleteInstruction(id);
                fetchInstructions();
            } catch (error) {
                console.error("Error deleting instruction:", error);
            }
        }
    };

    const isYouTubeUrl = (url) => {
        if (!url) return false;
        return url.includes('youtube.com') || url.includes('youtu.be');
    };

    const getYouTubeEmbedUrl = (url) => {
        if (!url) return '';
        let videoId = '';
        if (url.includes('v=')) {
            videoId = url.split('v=')[1].split('&')[0];
        } else if (url.includes('youtu.be/')) {
            videoId = url.split('youtu.be/')[1].split('?')[0];
        } else if (url.includes('embed/')) {
            videoId = url.split('embed/')[1].split('?')[0];
        }
        return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    };

    const getFullVideoUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        return `${backendUrl}${url}`;
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={onClose}
                    />

                    <motion.div
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        className="relative w-full max-w-4xl bg-white rounded shadow-2xl overflow-hidden flex flex-col max-h-[85vh] m-2"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-gray-200 shrink-0">
                            <div className="flex items-center space-x-3">
                                {activeVideo && !isEditing && (
                                    <button
                                        onClick={() => setActiveVideo(null)}
                                        className="p-2 hover:bg-gray-100 rounded-full transition-colors mr-1"
                                    >
                                        <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
                                    </button>
                                )}
                                <PlayIcon className="w-5 h-5 text-primary-blue fill-current" />
                                <h2 className="text-base font-bold text-gray-800 tracking-tight">
                                    {activeVideo ? instructions.find(i => i.id === activeVideo)?.title : t('instructions.title')}
                                </h2>
                            </div>
                            <div className="flex items-center space-x-3">
                                {isAdmin && !isEditing && !activeVideo && (
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className="flex items-center px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded transition-colors"
                                    >
                                        <PlusIcon className="w-4 h-4 mr-1.5" />
                                        {t('instructions.admin.add')}
                                    </button>
                                )}
                                <button
                                    onClick={onClose}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <XMarkIcon className="w-6 h-6 text-gray-400" />
                                </button>
                            </div>
                        </div>

                        {/* Content Area */}
                        <div className="flex-grow overflow-y-auto bg-gray-100 flex flex-col">
                            {isEditing ? (
                                <div className="p-6">
                                    <form onSubmit={handleSave} className="space-y-3 max-w-md mx-auto bg-white p-5 border border-gray-300 rounded shadow-sm">
                                        <h3 className="text-xs font-bold text-gray-400 uppercase border-b pb-2 mb-3">
                                            {editData.id ? t('instructions.admin.edit') : t('instructions.admin.add')}
                                        </h3>
                                        <div>
                                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">{t('instructions.admin.titleLabel')}</label>
                                            <input
                                                type="text"
                                                required
                                                value={editData.title}
                                                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                                                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-blue"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">{t('instructions.admin.videoLabel')}</label>
                                            <input
                                                type="file"
                                                accept="video/*"
                                                onChange={(e) => setEditData({ ...editData, file: e.target.files[0] })}
                                                className="w-full text-xs text-gray-500 mb-2"
                                            />
                                            <input
                                                type="text"
                                                placeholder="YouTube URL..."
                                                value={editData.videoUrl}
                                                onChange={(e) => setEditData({ ...editData, videoUrl: e.target.value })}
                                                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-blue"
                                            />
                                        </div>
                                        <div className="flex justify-end space-x-2 pt-2">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setIsEditing(false);
                                                    setEditData({ id: null, title: '', videoUrl: '', order: 0, file: null });
                                                }}
                                                className="px-3 py-1.5 text-xs font-bold text-gray-500 hover:text-gray-700"
                                            >
                                                {t('instructions.admin.cancel')}
                                            </button>
                                            <button
                                                type="submit"
                                                className="px-4 py-1.5 bg-primary-blue text-white text-xs font-bold rounded hover:bg-hover-blue"
                                            >
                                                {t('instructions.admin.save')}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            ) : activeVideo ? (
                                <div className="flex-grow flex flex-col bg-black relative min-h-[400px]">
                                    {isYouTubeUrl(instructions.find(i => i.id === activeVideo)?.videoUrl) ? (
                                        <iframe
                                            src={getYouTubeEmbedUrl(instructions.find(i => i.id === activeVideo)?.videoUrl)}
                                            className="w-full h-full flex-grow border-0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        />
                                    ) : (
                                        <video
                                            src={getFullVideoUrl(instructions.find(i => i.id === activeVideo)?.videoUrl)}
                                            controls
                                            autoPlay
                                            className="w-full h-full max-h-[75vh] object-contain flex-grow"
                                        />
                                    )}
                                </div>
                            ) : (
                                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {loading ? (
                                        <div className="col-span-full flex justify-center py-10">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-blue"></div>
                                        </div>
                                    ) : instructions.length === 0 ? (
                                        <div className="col-span-full text-center py-10 text-gray-400 text-xs italic">
                                            {t('instructions.noInstructions')}
                                        </div>
                                    ) : (
                                        instructions.map((item) => (
                                            <div
                                                key={item.id}
                                                className="group bg-white border border-gray-200 rounded overflow-hidden shadow-sm hover:border-primary-blue transition-colors cursor-pointer"
                                                onClick={() => setActiveVideo(item.id)}
                                            >
                                                <div className="relative w-full h-0 pb-[56.25%] bg-gray-900 border-b border-gray-100 italic flex items-center justify-center">
                                                    <div className="absolute inset-0 flex items-center justify-center bg-gray-800/20 group-hover:bg-black/30 transition-colors">
                                                        <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow">
                                                            <PlayIcon className="w-5 h-5 text-primary-blue fill-current" />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="p-2 flex items-center justify-between">
                                                    <span className="text-[12px] font-bold text-gray-700 truncate block flex-grow">
                                                        {item.title}
                                                    </span>
                                                    {isAdmin && (
                                                        <div className="flex items-center space-x-0.5 ml-2 shrink-0 bg-gray-50 rounded px-1" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                onClick={() => {
                                                                    setEditData({ id: item.id, title: item.title, videoUrl: item.videoUrl, order: item.order, file: null });
                                                                    setIsEditing(true);
                                                                }}
                                                                className="p-1 text-gray-400 hover:text-primary-blue"
                                                            >
                                                                <PencilIcon className="w-3 h-3" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(item.id)}
                                                                className="p-1 text-gray-400 hover:text-red-500"
                                                            >
                                                                <TrashIcon className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default InstructionModal;
